import type { Locale } from "../i18n/utils";
import { getAssessmentData, getFeedbackBundle } from "../i18n/utils";

export type FeedbackCategory = "bas" | "mid" | "haut";

// Question groups mirrored here to avoid circular imports with useAssessment
const GROUPS = [
  { start: 0, end: 4 },
  { start: 5, end: 10 },
  { start: 11, end: 16 },
  { start: 17, end: 22 },
] as const;

// bas:  count_n1 >= 2
// haut: count_n1 < 2 AND count_n4 >= 2
// mid:  fallback
export function getPillarCategory(
  answers: Record<number, string>,
  groupIndex: number,
  locale: Locale = "fr",
): FeedbackCategory {
  const { start, end } = GROUPS[groupIndex];
  const questions = getAssessmentData(locale);
  let countN1 = 0;
  let countN4 = 0;
  for (let i = start; i <= end; i++) {
    const answerId = answers[i];
    if (!answerId) continue;
    const option = questions[i].options.find((o) => o.id === answerId);
    if (!option) continue;
    if (option.level === 1) countN1++;
    if (option.level === 4) countN4++;
  }
  if (countN1 >= 2) return "bas";
  if (countN4 >= 2) return "haut";
  return "mid";
}

export function getPillarFeedback(
  pillarNum: 1 | 2 | 3 | 4,
  category: FeedbackCategory,
  locale: Locale = "fr",
): string {
  return getFeedbackBundle(locale).pillarFeedbacks[pillarNum][category];
}

// --- Cross-pillar feedbacks after pillar 2 (9 combinations) ---

type Cat = FeedbackCategory;

export function getCrossFeedbackP2(
  s: Cat,
  m: Cat,
  locale: Locale = "fr",
): string {
  return getFeedbackBundle(locale).crossP2[s][m];
}

// --- Cross-pillar feedbacks after pillar 3 (27 combinations → 3 patterns) ---

const CAT_TO_NUM: Record<FeedbackCategory, number> = {
  bas: 1,
  mid: 2,
  haut: 3,
};

export function getCrossFeedbackP3(
  s: Cat,
  m: Cat,
  c: Cat,
  locale: Locale = "fr",
): string {
  const bundle = getFeedbackBundle(locale);
  if (s === m && m === c) {
    return bundle.crossP3Uniforme[s];
  }

  const sn = CAT_TO_NUM[s];
  const mn = CAT_TO_NUM[m];
  const cn = CAT_TO_NUM[c];
  const minVal = Math.min(sn, mn, cn);
  const countAtMin = [sn, mn, cn].filter((v) => v === minVal).length;

  if (countAtMin === 1) {
    if (sn === minVal) return bundle.crossP3EnRetrait.strategie;
    if (mn === minVal) return bundle.crossP3EnRetrait.marque;
    return bundle.crossP3EnRetrait.contenu;
  }

  const maxVal = Math.max(sn, mn, cn);
  if (sn === maxVal) return bundle.crossP3EnAvance.strategie;
  if (mn === maxVal) return bundle.crossP3EnAvance.marque;
  return bundle.crossP3EnAvance.contenu;
}

// --- Combined feedback: pillar + cross (what ThemeComplete renders) ---

export function getThemeFeedback(
  pillarNum: number,
  answers: Record<number, string>,
  locale: Locale = "fr",
): string {
  if (pillarNum === 1) {
    const cat = getPillarCategory(answers, 0, locale);
    return getPillarFeedback(1, cat, locale);
  }

  if (pillarNum === 2) {
    const scat = getPillarCategory(answers, 0, locale);
    const mcat = getPillarCategory(answers, 1, locale);
    const pillarText = getPillarFeedback(2, mcat, locale);
    const crossText = getCrossFeedbackP2(scat, mcat, locale);
    return `${pillarText}\n\n${crossText}`;
  }

  if (pillarNum === 3) {
    const scat = getPillarCategory(answers, 0, locale);
    const mcat = getPillarCategory(answers, 1, locale);
    const ccat = getPillarCategory(answers, 2, locale);
    const pillarText = getPillarFeedback(3, ccat, locale);
    const crossText = getCrossFeedbackP3(scat, mcat, ccat, locale);
    return `${pillarText}\n\n${crossText}`;
  }

  return "";
}
